import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart2, Users, Brain, AlertCircle, Check, Copy, Sparkles, RefreshCw, Flag, PlayCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { supabase } from '../lib/supabase';
import { nanoid } from 'nanoid';
import type { Entry, Participant, AISummary } from '../lib/types';

interface SessionStatusProps {
  entries: Entry[];
  participants: Participant[];
  maxParticipants: number;
  onRequestSummary?: (forceNew?: boolean) => void;
  onFinishSession?: () => void;
  onResumeSession?: () => void;
  isViewOnly?: boolean;
  summary?: AISummary | null;
  isFinished?: boolean;
  isCreator?: boolean;
  sessionId: string;
  participantId?: string;
}

export function SessionStatus({ 
  entries, 
  participants, 
  maxParticipants, 
  onRequestSummary, 
  onFinishSession,
  onResumeSession,
  isViewOnly = false,
  summary,
  isFinished = false,
  isCreator = false,
  sessionId,
  participantId
}: SessionStatusProps) {
  const { t } = useTranslation();
  const [showTip, setShowTip] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const navigate = useNavigate();
  const totalLetters = 26;
  const usedLetters = new Set(entries.map(entry => entry.letter.toUpperCase())).size;
  const letterProgress = (usedLetters / totalLetters) * 100;
  const participantProgress = (participants.length / maxParticipants) * 100;
  const minEntriesForAnalysis = 5;
  const hasEnoughEntries = entries.length >= minEntriesForAnalysis;
  const isBetaPhase = import.meta.env.VITE_BETA_PHASE === 'true';

  const handleCopyLink = async () => {
    try {
      if (!sessionId || !participantId) return;
      
      // Focus the document before attempting to write to clipboard
      window.focus();

      // Create invite token
      const token = nanoid(10);
      const { error: inviteError } = await supabase
        .from('session_invites')
        .insert({
          session_id: sessionId,
          token,
          created_by: participantId
        });

      if (inviteError) throw inviteError;

      // Generate and copy join URL
      const joinUrl = new URL(window.location.origin);
      joinUrl.pathname = `/join/${token}`;

      try {
        await navigator.clipboard.writeText(joinUrl.toString());
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      } catch (clipboardError) {
        // Fallback: Create a temporary input element
        const tempInput = document.createElement('input');
        tempInput.value = joinUrl.toString();
        document.body.appendChild(tempInput);
        tempInput.select();
        document.execCommand('copy');
        document.body.removeChild(tempInput);
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      }
    } catch (err) {
      console.error('Error creating invite link:', err);
    }
  };

  const handleAnalysisClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!hasEnoughEntries || !onRequestSummary) return;
    onRequestSummary(false);
  };

  const handleNewAnalysisClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!onRequestSummary) return;
    onRequestSummary(true);
  };

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {/* Progress Card - Always show */}
      <div className="rounded-lg bg-white/80 p-4 shadow-sm backdrop-blur-sm">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <BarChart2 className="h-5 w-5 text-gray-400" />
            <span className="font-medium text-gray-700">{t('session.progress')}</span>
          </div>
          <div className="flex flex-col items-end text-sm text-gray-500">
            <span>{usedLetters} {t('session.participants.limit')} {totalLetters} {t('session.letters')}</span>
            <span>{entries.length} {t('session.words')}</span>
          </div>
        </div>
        <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-gray-100">
          <div
            className="h-full rounded-full bg-gray-300 transition-all duration-500"
            style={{ width: `${letterProgress}%`, opacity: 0.7 }}
          />
        </div>
        {letterProgress < 50 && showTip && !isViewOnly && !isFinished && (
          <div className="mt-3 flex items-start gap-2 rounded-lg bg-gray-50 p-2 text-xs text-gray-600">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            <p>{t('session.tip.difficult_letters')}</p>
          </div>
        )}
      </div>

      {/* Middle Card - Show either Participants or Start Session button */}
      {isViewOnly ? (
        <div className="rounded-lg bg-white/80 p-4 shadow-sm backdrop-blur-sm">
          <div className="flex items-center gap-2 mb-3">
            <Brain className="h-5 w-5 text-gray-400" />
            <span className="font-medium text-gray-700">{t('session.start_own')}</span>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            {t('session.start_own.description')}
          </p>
          <button
            onClick={() => navigate('/')}
            className="w-full flex items-center justify-center gap-2 rounded-lg bg-accent-500 px-4 py-2 text-white hover:bg-accent-600 transition-colors"
          >
            <Sparkles className="h-5 w-5" />
            <span>{t('session.start_new')}</span>
          </button>
        </div>
      ) : (
        <div className="rounded-lg bg-white/80 p-4 shadow-sm backdrop-blur-sm">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-gray-400" />
              <span className="font-medium text-gray-700">{t('session.participants')}</span>
            </div>
            <span className="text-sm text-gray-500">
              {participants.length} {t('session.participants.limit')} {maxParticipants}
            </span>
          </div>
          <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-gray-100">
            <div
              className="h-full rounded-full bg-gray-300 transition-all duration-500"
              style={{ width: `${participantProgress}%`, opacity: 0.7 }}
            />
          </div>
          {!isFinished && participants.length < maxParticipants && (
            <button
              onClick={handleCopyLink}
              className="mt-3 w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 flex items-center justify-center gap-2 transition-colors"
            >
              {copySuccess ? (
                <>
                  <Check className="h-4 w-4" />
                  {t('session.invite.copied')}
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4" />
                  {t('session.invite')}
                </>
              )}
            </button>
          )}
          {isCreator && !isFinished && (
            <button
              onClick={onFinishSession}
              className="mt-2 w-full flex items-center justify-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-4 py-2 text-gray-600 hover:bg-gray-100 transition-colors"
            >
              <Flag className="h-4 w-4" />
              <span>{t('session.finish')}</span>
            </button>
          )}
          {isCreator && isFinished && (
            <button
              onClick={onResumeSession}
              className="mt-2 w-full flex items-center justify-center gap-2 rounded-lg bg-primary-500 px-4 py-2 text-white hover:bg-primary-600 transition-colors"
            >
              <PlayCircle className="h-4 w-4" />
              <span>{t('session.resume')}</span>
            </button>
          )}
          {isFinished && (
            <div className="mt-3 text-center text-sm text-accent-600">
              {t('session.finished')}
            </div>
          )}
        </div>
      )}

      {/* AI Analysis Card - Always show */}
      <div className="rounded-lg bg-white/80 p-4 shadow-sm backdrop-blur-sm sm:col-span-2 lg:col-span-1">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Brain className="h-5 w-5 text-gray-400" />
            <span className="font-medium text-gray-700">{t('analysis.title')}</span>
          </div>
        </div>
        <div className="space-y-3">
          <p className="text-sm text-gray-600">
            {summary?.payment_status === 'completed' 
              ? t('analysis.available')
              : t('analysis.description')}
          </p>
          <div className="space-y-2">
            <button
              onClick={handleAnalysisClick}
              disabled={!hasEnoughEntries}
              className="relative w-full group disabled:opacity-70 disabled:cursor-not-allowed"
            >
              <div className="absolute -inset-0.5 bg-gradient-to-r from-primary-500 to-accent-500 rounded-lg blur opacity-60 group-hover:opacity-100 transition duration-1000 group-hover:duration-200"></div>
              <div className="relative flex items-center justify-center gap-2 px-4 py-2 bg-white rounded-lg leading-none">
                {!hasEnoughEntries ? (
                  <>
                    <Sparkles className="h-5 w-5 text-primary-500" />
                    <span className="font-medium bg-clip-text text-transparent bg-gradient-to-r from-primary-500 to-accent-500">
                      {t('analysis.entries_needed', { count: minEntriesForAnalysis - entries.length })}
                    </span>
                  </>
                ) : summary?.payment_status === 'completed' ? (
                  <>
                    <Brain className="h-5 w-5 text-primary-500" />
                    <span className="font-medium bg-clip-text text-transparent bg-gradient-to-r from-primary-500 to-accent-500">
                      {t('analysis.show')}
                    </span>
                  </>
                ) : (
                  <>
                    <Sparkles className="h-5 w-5 text-primary-500" />
                    <span className="font-medium bg-clip-text text-transparent bg-gradient-to-r from-primary-500 to-accent-500">
                      {t('analysis.request')} {!isBetaPhase && t('analysis.price')}
                    </span>
                  </>
                )}
              </div>
            </button>
            {summary?.payment_status === 'completed' && (
              <button
                onClick={handleNewAnalysisClick}
                className="w-full flex items-center justify-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 transition-colors"
              >
                <RefreshCw className="h-4 w-4" />
                <span>{t('analysis.request_new')} {!isBetaPhase && t('analysis.price')}</span>
              </button>
            )}
          </div>
          {!summary?.payment_status && (
            <p className="text-xs text-center text-gray-500">
              {isBetaPhase ? t('analysis.beta_free') : t('analysis.price_info')}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}